import { useContext, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import useCreateResults from 'components/Dashboard/ListControl/useCreateResults';
import useGeneralRequestProperties from 'components/Dashboard/hooks/useGeneralRequestProperties';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

const useCallParliamentLive = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const { sectionsView, keywordsLists } = state;
  const { selectedOptionParliamentLiveView } = sectionsView ?? {};
  const { parliamentLiveReferenceDates, eventApiCall, condition } = props ?? {};

  const { createRequestProperties } = useGeneralRequestProperties();
  const sectionName = createSectionName();
  const params = useParams();
  const { event } = params ?? {};
  const topicParam = getUrlParam('topic-id');
  const searchParam = getUrlParam('search') ?? getUrlParam('or');

  const ParliamentLive = contentResults?.ParliamentLive;
  const { createResults } = useCreateResults({});

  const { contentFilters } = useFilters();
  const defaultInterval = 30000; //ms

  const minutesPerCallParam = getUrlParam('minutesPerCall');
  const minutesPerCall = minutesPerCallParam ?? defaultInterval / 60000;
  const interval = minutesPerCallParam ? 60000 : defaultInterval;
  const [date, setDate] = useState(null);
  const intervallCallFunction = useRef(null);
  intervallCallFunction.current = async ({ source }) => {
    try {
      const directTopic = keywordsLists?.find((item) =>
        topicParam === 'all' ? item?.id === null : item?.id === parseInt(topicParam)
      );
      const { initialCallFirstDateValue, initialCallSecondDateValue } =
        parliamentLiveReferenceDates ?? ParliamentLive?.parliamentLiveReferenceDates;
      const { requestProperties } = createRequestProperties({
        pageNumber: 1,
        noUseQuery: false,
        customPageSize: 10000,
        directTopic,
      });
      let newRequestProperties = { ...requestProperties };
      const dateValue =
        selectedOptionParliamentLiveView === 'Relevant remarks' || !!event
          ? (date ?? initialCallSecondDateValue)
          : initialCallFirstDateValue;

      const secondValue = dayjs(date ?? initialCallSecondDateValue)
        .add(minutesPerCall, 'minute')
        .format('YYYY-MM-DDTHH:mm:ss');

      const newDateFilter = {
        field: 'createdAtUtc',
        value: dateValue,
        operator: 'DATE_GTE',
      };

      let newFilters = [...newRequestProperties.filters];
      newFilters = [
        ...newFilters.filter((item) => item?.field !== 'createdAtUtc'),
        newDateFilter,
        {
          field: 'createdAtUtc',
          value: secondValue,
          operator: 'date_lt',
        },
      ];
      newRequestProperties = { ...newRequestProperties, filters: newFilters };
      if (eventApiCall) {
        await eventApiCall({
          propsRequestProperties: newRequestProperties,
          source,
        });
      } else {
        await createResults({
          pageNumber: 1,
          setMarker: false,
          source,
          notForceLoading: true,
          requestProperties: newRequestProperties,
          fromInterval: true,
        });
      }
      setDate(secondValue);
    } catch (error) {}
  };
  const intervalFunc = useRef(null);
  useEffect(() => {
    const source = new AbortController();
    if ((!event && sectionName === 'ParliamentLive') || (!!event && condition)) {
      intervalFunc.current = setInterval(() => intervallCallFunction.current({ source }), interval);
    }
    return () => {
      if (!!source) {
        source.abort('New ParliamentLive call canceled by the user.');
      }
      if (!!intervalFunc.current) {
        clearInterval(intervalFunc.current);
      }
      setDate(null);
    };
  }, [
    sectionName,
    ParliamentLive?.loaded,
    selectedOptionParliamentLiveView,
    condition,
    topicParam,
    searchParam,
    contentFilters,
  ]);
};

export default useCallParliamentLive;
